import React, { Reducer } from 'react';
import { UserRecord } from '../dto/User';
import { ClientPrincipal } from '../util/ClientPrincipal';
import { Publisher } from '../dto/Publisher';
import { CookieSettings } from '../components/CookieBanner';

type GlobalStateAction = { type: "setError", payload: Error | undefined }
    | { type: "setAuth", payload: ClientPrincipal | null }
    | { type: "setUser", payload: UserRecord }
    | { type: "setPublishers", payload: Publisher[] | null }
    | { type: "clearUser", payload: null }
    | { type: "setCookieSettings", payload: CookieSettings }
    | { type: "setShowCookieBanner", payload: boolean };

export type GlobalStateDispatch = (action: GlobalStateAction) => void;

export type GlobalStateProps = {
    error: Error | undefined;
    auth: ClientPrincipal | null | undefined;
    user: UserRecord | null;
    publishers: Publisher[] | null;
    cookieSettings?: CookieSettings;
    showCookieBanner?: boolean;
};

const stateReducer: Reducer<GlobalStateProps, GlobalStateAction> = (state, action): GlobalStateProps => {
    switch (action.type)
    {
        case "setError":
            return { ...state, error: action.payload };
        case "setAuth":
            return { ...state, auth: action.payload };
        case "setUser":
            return { ...state, user: action.payload };
        case "setPublishers":
            return { ...state, publishers: action.payload };
        case "clearUser":
            return { ...state, user: null };
        case "setCookieSettings":
            return { ...state, cookieSettings: action.payload, showCookieBanner: false };
        case "setShowCookieBanner":
            return { ...state, showCookieBanner: action.payload };
    }
}

const GlobalState = React.createContext<GlobalStateProps | undefined>(undefined);
const GlobalDispatch = React.createContext<GlobalStateDispatch | undefined>(undefined);

export const GlobalStateProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [state, dispatch] = React.useReducer(stateReducer, { error: undefined, auth: undefined, user: null, publishers: null });

    return (
        <GlobalState.Provider value={state}>
            <GlobalDispatch.Provider value={dispatch}>
                { children }
            </GlobalDispatch.Provider>
        </GlobalState.Provider>
    );
};

export const useGlobalState = () => {
    const context = React.useContext(GlobalState);
  
    if (!context) {
      throw new Error(
        "useGlobalState has to be used within <GlobalState.Provider>"
      );
    }
  
    return context;
};

export const useGlobalDispatch = () => {
    const context = React.useContext(GlobalDispatch);

    if (!context) {
        throw new Error(
            "useGlobalDispatch has to be used within <GlobalDispatch.Provider>"
        );
    }

    return context;
};

export const useGlobalContext = (): [GlobalStateProps, GlobalStateDispatch] => [ useGlobalState(), useGlobalDispatch() ];